import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, Send, ArrowLeft, Loader2, WifiOff, User, AlertCircle, Lock, Smile, X, Reply, Search, Type, Users, Pin, ShieldAlert, MoreVertical, Trash2, Clock, Ban, Shield, AlertTriangle, PinOff, ImagePlus, Pencil, CornerUpLeft } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { useChat } from '../hooks/useChat';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'med_chat_user';
const MAX_MSG_LENGTH = 2000;

/* ═══════════ Admin Role Detection (UI-only) ═══════════ */
function getAdminRole() {
  try {
    const token = localStorage.getItem('med_admin_token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.type === 'admin') return 'admin';
    if (payload.role === 'moderator') return 'moderator';
    return null;
  } catch { return null; }
}

/* ═══════════ GIF Selector ═══════════ */
const GifSelector = ({ userId, onSend, onClose, replyToId }) => {
  const [gifs, setGifs] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const [gifError, setGifError] = useState('');

  // Load trending on mount
  useEffect(() => {
    setLoading(true);
    setGifError('');
    fetch(`${API}/api/chat/gifs/trending?limit=24`)
      .then(r => { if (!r.ok) throw new Error('service'); return r.json(); })
      .then(d => { setGifs(d.gifs || []); setLoading(false); })
      .catch(() => { setLoading(false); setGifError('GIF service temporarily unavailable.'); });
    searchRef.current?.focus();
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      // Load trending when search is cleared
      setLoading(true);
      setGifError('');
      fetch(`${API}/api/chat/gifs/trending?limit=24`)
        .then(r => { if (!r.ok) throw new Error('service'); return r.json(); })
        .then(d => { setGifs(d.gifs || []); setLoading(false); })
        .catch(() => { setLoading(false); setGifError('GIF service temporarily unavailable.'); });
      return;
    }
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      setGifError('');
      fetch(`${API}/api/chat/gifs/search?q=${encodeURIComponent(query.trim())}&limit=24`)
        .then(r => { if (!r.ok) throw new Error('service'); return r.json(); })
        .then(d => { setGifs(d.gifs || []); setLoading(false); })
        .catch(() => { setLoading(false); setGifError('GIF service temporarily unavailable.'); });
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const handleSelect = async (gif) => {
    setSending(gif.id);
    try {
      const body = { user_id: userId, gif_url: gif.url };
      if (replyToId) body.reply_to_id = replyToId;
      const res = await fetch(`${API}/api/chat/messages/gif`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send GIF');
      onSend();
    } catch (err) {
      toast.error(err.message);
    }
    setSending(null);
  };

  return (
    <div className="absolute bottom-full left-0 right-0 mb-0 bg-black/95 border border-white/10 rounded-t-2xl overflow-hidden z-50 flex flex-col" style={{ maxHeight: '55vh' }} data-testid="gif-selector">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
        <div className="flex-1 flex items-center gap-2 bg-white/5 rounded-lg px-2.5 py-1.5">
          <Search size={13} className="text-white/25 flex-shrink-0" />
          <input ref={searchRef} value={query} onChange={e => setQuery(e.target.value)} placeholder="Search GIFs..."
            className="bg-transparent text-white text-xs w-full outline-none placeholder-white/25" data-testid="gif-search-input" />
          {query && <button onClick={() => setQuery('')} className="text-white/20"><X size={12} /></button>}
        </div>
        <button onClick={onClose} className="text-white/25 hover:text-white/50 p-1" data-testid="gif-close">
          <X size={16} />
        </button>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto p-2" data-testid="gif-grid">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={18} className="animate-spin text-white/20" />
          </div>
        ) : gifError ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2" data-testid="gif-error">
            <AlertCircle size={18} className="text-amber-400/60" />
            <p className="text-center text-amber-400/60 text-xs">{gifError}</p>
          </div>
        ) : gifs.length === 0 ? (
          <p className="text-center text-white/20 text-xs py-8" data-testid="gif-empty">No GIFs found</p>
        ) : (
          <div className="grid grid-cols-2 gap-1.5">
            {gifs.map(gif => (
              <button key={gif.id} onClick={() => handleSelect(gif)} disabled={!!sending}
                className="relative rounded-lg overflow-hidden bg-white/5 hover:ring-2 hover:ring-[#E91E8D]/40 transition-all aspect-video"
                data-testid={`gif-item-${gif.id}`}>
                <img src={gif.preview_url || gif.url} alt={gif.title} loading="lazy"
                  className="w-full h-full object-cover" />
                {sending === gif.id && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <Loader2 size={16} className="animate-spin text-[#E91E8D]" />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
        <p className="text-center text-white/10 text-[8px] mt-2 pb-1">Powered by GIPHY</p>
      </div>
    </div>
  );
};

/* ═══════════ Emoji Picker (lazy loaded) ═══════════ */
let EmojiPickerComponent = null;
let emojiData = null;

const LazyEmojiPicker = ({ onSelect, onClose }) => {
  const [ready, setReady] = useState(!!EmojiPickerComponent);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!EmojiPickerComponent) {
      Promise.all([
        import('@emoji-mart/react'),
        import('@emoji-mart/data'),
      ]).then(([mod, datamod]) => {
        EmojiPickerComponent = mod.default;
        emojiData = datamod.default;
        setReady(true);
      });
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, [onClose]);

  if (!ready) return (
    <div className="absolute bottom-full right-0 mb-2 bg-black/95 border border-white/10 rounded-xl p-4">
      <Loader2 size={18} className="animate-spin text-white/30" />
    </div>
  );

  return (
    <div ref={pickerRef} className="absolute bottom-full right-0 mb-2 z-50" data-testid="emoji-picker">
      <EmojiPickerComponent
        data={emojiData}
        onEmojiSelect={(emoji) => onSelect(emoji.native)}
        theme="dark"
        previewPosition="none"
        skinTonePosition="none"
        maxFrequentRows={2}
        perLine={8}
        emojiSize={28}
        emojiButtonSize={36}
      />
    </div>
  );
};

/* ═══════════ Identity Screen ═══════════ */
const IdentityScreen = ({ onRegister, loading }) => {
  const [name, setName] = useState('');

  const handleNamed = (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Please enter a display name'); return; }
    onRegister(name.trim(), false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-black" data-testid="chat-identity-screen">
      <SEO title="Mediterranean Live Chat" description="Join the Mediterranean Live Chat." url="/chat" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="glass-card p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-[#E91E8D]/10 border border-[#E91E8D]/20 flex items-center justify-center">
            <MessageCircle size={26} className="text-[#E91E8D]" />
          </div>
          <p className="text-white/25 text-[10px] font-bold tracking-widest uppercase mb-2">Welcome to</p>
          <h1 className="font-display text-2xl font-bold text-white mb-2" data-testid="chat-welcome-title">Mediterranean Live Chat</h1>
          <p className="text-white/40 text-xs mb-8">Choose how you'd like to appear.</p>

          <form onSubmit={handleNamed} className="space-y-3 mb-4">
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="Display name" maxLength={30}
              className="input-med w-full !py-3 !text-sm text-center" autoFocus
              data-testid="chat-name-input"
            />
            <button type="submit" disabled={loading || !name.trim()}
              className="btn-pink w-full !text-sm justify-center" data-testid="chat-enter-named">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <User size={16} />}
              Enter Chat
            </button>
          </form>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/20 text-[10px] uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button onClick={() => onRegister(null, true)} disabled={loading}
            className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-white/50 text-sm font-semibold hover:bg-white/10 hover:text-white/70 transition-all"
            data-testid="chat-enter-anonymous">
            Continue Anonymously
          </button>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-xs transition-colors" data-testid="chat-back-home">
            <ArrowLeft size={12} /> Back to Home
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

/* ═══════════ Chat Disabled Screen ═══════════ */
const ChatClosedScreen = () => (
  <div className="min-h-screen flex items-center justify-center px-4 bg-black" data-testid="chat-closed">
    <SEO title="Mediterranean Live Chat" url="/chat" />
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
      <div className="glass-card p-8 text-center">
        <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
          <AlertCircle size={26} className="text-white/30" />
        </div>
        <h2 className="font-display text-xl font-bold text-white mb-3">Live Chat Closed</h2>
        <p className="text-white/40 text-sm leading-relaxed mb-6">
          Mediterranean Live Chat is currently unavailable. Please check back later.
        </p>
        <Link to="/" className="btn-pink !text-sm inline-flex items-center gap-2 justify-center" data-testid="chat-closed-home">
          <ArrowLeft size={14} /> Back to Home
        </Link>
      </div>
    </motion.div>
  </div>
);

/* ═══════════ Paywall / Unlock Screen ═══════════ */
const PaywallScreen = ({ user, onAccessGranted }) => {
  const [loading, setLoading] = useState(null); // null | 'day_pass' | 'monthly'
  const [confirmPhase, setConfirmPhase] = useState(null); // null | 'polling' | 'extended'
  const pollRef = useRef(null);
  const [showRestore, setShowRestore] = useState(false);
  const [restoreEmail, setRestoreEmail] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [searchParams] = useSearchParams();

  // Helper: start subscription confirmation polling
  const startConfirmationPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    setConfirmPhase('polling');
    let attempts = 0;
    const maxAttempts = 30; // 60 seconds of fast polling
    const pendingDpId = searchParams.get('dp_id') || localStorage.getItem('med_pending_dp_id') || sessionStorage.getItem('med_pending_dp_id');
    const pendingDpOrder = localStorage.getItem('med_pending_dp_order') || sessionStorage.getItem('med_pending_dp_order');

    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        // Day pass polling
        if (pendingDpId && pendingDpOrder) {
          const dpRes = await fetch(`${API}/api/chat/day-pass/${pendingDpId}/status?order_id=${pendingDpOrder}`);
          const dpData = await dpRes.json();
          if (dpData.status === 'active') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            localStorage.removeItem('med_pending_dp_id');
            localStorage.removeItem('med_pending_dp_order');
            localStorage.removeItem('med_pending_sub');
            sessionStorage.removeItem('med_pending_dp_id');
            sessionStorage.removeItem('med_pending_dp_order');
            toast.success('Day Pass activated! You have 24 hours of chat access.');
            const accessRes = await fetch(`${API}/api/chat/access/${user.id}`, { credentials: 'same-origin' });
            const accessData = await accessRes.json();
            if (accessData.has_access) onAccessGranted(accessData);
            setConfirmPhase(null);
            return;
          }
        }
        // Subscription status check (authoritative endpoint with built-in reconciliation)
        const res = await fetch(`${API}/api/chat/subscription-status/${user.id}`, { credentials: 'same-origin' });
        const data = await res.json();
        if (data.status === 'active' && data.has_access) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          localStorage.removeItem('med_pending_sub');
          localStorage.removeItem('med_pending_dp_id');
          localStorage.removeItem('med_pending_dp_order');
          sessionStorage.removeItem('med_pending_dp_id');
          sessionStorage.removeItem('med_pending_dp_order');
          toast.success('Chat access activated!');
          if (data.redirect_to) {
            // Canonical identity found — reload via /me to get correct user data
            const meRes = await fetch(`${API}/api/chat/me`, { credentials: 'same-origin' });
            const meData = await meRes.json();
            if (meData.user && meData.has_access) onAccessGranted(meData);
          } else {
            onAccessGranted(data);
          }
          setConfirmPhase(null);
          return;
        }
        // After initial fast polling, transition to extended state (stop auto-polling)
        if (attempts >= maxAttempts) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setConfirmPhase('extended');
        }
      } catch {
        if (attempts >= maxAttempts) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setConfirmPhase('extended');
        }
      }
    }, 2000);
  }, [searchParams, user.id, onAccessGranted]);

  // Manual check for extended state
  const handleCheckAgain = async () => {
    setConfirmPhase('polling');
    startConfirmationPolling();
  };

  // User explicitly abandons pending checkout
  const handleStartFresh = () => {
    localStorage.removeItem('med_pending_sub');
    localStorage.removeItem('med_pending_dp_id');
    localStorage.removeItem('med_pending_dp_order');
    sessionStorage.removeItem('med_pending_dp_id');
    sessionStorage.removeItem('med_pending_dp_order');
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setConfirmPhase(null);
  };

  // On mount: check if returning from checkout or have pending subscription
  useEffect(() => {
    const isCheckoutReturn = searchParams.get('checkout') === 'complete';
    const pendingStr = localStorage.getItem('med_pending_sub');
    let hasPending = false;
    if (pendingStr) {
      try {
        const pending = JSON.parse(pendingStr);
        if (pending.user_id === user.id) {
          const age = Date.now() - new Date(pending.created_at).getTime();
          if (age < 3600000) { // Less than 1 hour
            hasPending = true;
          } else {
            localStorage.removeItem('med_pending_sub');
          }
        }
      } catch { localStorage.removeItem('med_pending_sub'); }
    }
    if (isCheckoutReturn || hasPending) {
      startConfirmationPolling();
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [searchParams, user.id, startConfirmationPolling]);

  const handleDayPass = async () => {
    setLoading('day_pass');
    try {
      // Create pending day pass
      const pendingRes = await fetch(`${API}/api/chat/day-pass`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id }),
      });
      const pendingData = await pendingRes.json();
      if (!pendingRes.ok) throw new Error(pendingData.detail || 'Failed to create day pass');

      // Create checkout via existing store checkout
      const checkoutRes = await fetch(`${API}/api/store/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variation_id: pendingData.variation_id,
          quantity: 1,
          entitlement_type: 'chat_day_pass',
          redirect_path: `/chat?dp_id=${pendingData.pending_id}`,
          pending_submission_id: pendingData.pending_id,
        }),
      });
      const checkoutData = await checkoutRes.json();
      if (!checkoutRes.ok) throw new Error(checkoutData.detail || 'Failed to create checkout');

      localStorage.setItem('med_pending_dp_id', pendingData.pending_id);
      localStorage.setItem('med_pending_dp_order', checkoutData.order_id);
      window.location.href = checkoutData.url;
    } catch (err) {
      toast.error(err.message);
      setLoading(null);
    }
  };

  const handleMonthly = async () => {
    // Guard: don't create a new checkout if one is already pending
    const pendingStr = localStorage.getItem('med_pending_sub');
    if (pendingStr) {
      try {
        const pending = JSON.parse(pendingStr);
        const age = Date.now() - new Date(pending.created_at).getTime();
        if (age < 1800000 && pending.user_id === user.id) {
          // Recent pending checkout exists — show confirmation screen instead
          startConfirmationPolling();
          return;
        }
      } catch {}
    }
    setLoading('monthly');
    try {
      const res = await fetch(`${API}/api/chat/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to start checkout');
      // If server reconciled an existing subscription, grant access immediately
      if (data.already_active) {
        toast.success(data.message || 'Your subscription has been activated!');
        if (data.redirect_to) {
          // Subscription found on canonical identity — reload via /me (server switches cookie)
          const meRes = await fetch(`${API}/api/chat/me`, { credentials: 'same-origin' });
          const meData = await meRes.json();
          if (meData.user && meData.has_access) onAccessGranted(meData);
        } else {
          const accessRes = await fetch(`${API}/api/chat/access/${user.id}`, { credentials: 'same-origin' });
          const accessData = await accessRes.json();
          if (accessData.has_access) onAccessGranted(accessData);
        }
        setLoading(null);
        return;
      }
      // Save pending subscription state before redirect (survives page refresh)
      localStorage.setItem('med_pending_sub', JSON.stringify({
        user_id: user.id,
        order_id: data.order_id,
        created_at: new Date().toISOString(),
      }));
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.message);
      setLoading(null);
    }
  };

  const handleRestoreRequest = async (e) => {
    e.preventDefault();
    if (!restoreEmail.trim()) return;
    setRestoring(true);
    try {
      const res = await fetch(`${API}/api/chat/recovery/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: restoreEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Unable to send verification code.');
      toast.success('Check your email for a verification code.');
      setOtpSent(true);
    } catch (err) {
      toast.error(err.message);
    }
    setRestoring(false);
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    if (!otpCode.trim()) return;
    setOtpVerifying(true);
    try {
      const res = await fetch(`${API}/api/chat/recovery/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email: restoreEmail.trim(), code: otpCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verification failed.');
      if (data.restored && data.has_access) {
        toast.success('Access restored!');
        const meRes = await fetch(`${API}/api/chat/me`, { credentials: 'same-origin' });
        const meData = await meRes.json();
        if (meData.user) {
          onAccessGranted(meData);
        }
      }
    } catch (err) {
      toast.error(err.message);
    }
    setOtpVerifying(false);
  };

  if (confirmPhase) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-black" data-testid="chat-verifying">
        <SEO title="Mediterranean Live Chat" url="/chat" />
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full max-w-sm text-center">
          <div className="glass-card p-8">
            <Loader2 size={32} className={`mx-auto mb-5 animate-spin ${confirmPhase === 'extended' ? 'text-amber-400' : 'text-[#E91E8D]'}`} />
            <h2 className="font-display text-xl font-bold text-white mb-2" data-testid="confirm-title">
              {confirmPhase === 'extended' ? 'Still Confirming...' : 'Confirming Your Subscription'}
            </h2>
            <p className="text-white/40 text-sm mb-1">
              {confirmPhase === 'extended'
                ? 'This is taking a bit longer than usual. Your payment is safe.'
                : 'Please wait while we verify your payment and activate your access...'}
            </p>
            {confirmPhase === 'extended' && (
              <div className="space-y-3 mt-5">
                <button onClick={handleCheckAgain}
                  className="w-full bg-[#E91E8D]/20 hover:bg-[#E91E8D]/30 border border-[#E91E8D]/30 text-white font-semibold text-sm rounded-xl px-4 py-3 transition-all flex items-center justify-center gap-2"
                  data-testid="btn-check-again">
                  Check Again
                </button>
                <button onClick={handleStartFresh}
                  className="text-white/20 hover:text-white/40 text-[11px] transition-colors"
                  data-testid="btn-start-fresh">
                  I didn't complete checkout — start over
                </button>
                <button onClick={() => { handleStartFresh(); setShowRestore(true); }}
                  className="text-[#E91E8D]/50 hover:text-[#E91E8D] text-[11px] transition-colors mt-1"
                  data-testid="btn-restore-from-confirm">
                  Already paid? Restore with email
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  const isReturning = user.subscription_status && user.subscription_status !== 'none';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-black" data-testid="chat-paywall">
      <SEO title="Mediterranean Live Chat" url="/chat" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#E91E8D]/10 border border-[#E91E8D]/20 flex items-center justify-center">
            <Lock size={26} className="text-[#E91E8D]" />
          </div>
          <p className="text-white/25 text-[10px] font-bold tracking-widest uppercase mb-2">
            {isReturning ? `Welcome back, ${user.display_name}` : 'Mediterranean Live Chat'}
          </p>
          <h2 className="font-display text-2xl font-bold text-white mb-1" data-testid="paywall-title">Choose Your Access</h2>
          <p className="text-white/30 text-xs">Select a plan to join the chat.</p>
        </div>

        {isReturning && user.subscription_status === 'canceled' && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 mb-4">
            <p className="text-amber-400/80 text-xs text-center">Your previous subscription has ended. Choose a plan to regain access.</p>
          </div>
        )}
        {isReturning && user.subscription_status === 'past_due' && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 mb-4">
            <p className="text-red-400/80 text-xs text-center">Your payment could not be processed. Choose a plan to restore access.</p>
          </div>
        )}

        {/* Day Pass Option */}
        <div className="glass-card p-5 mb-3" data-testid="option-day-pass">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-white font-bold text-sm">Day Pass</h3>
              <p className="text-white/25 text-[10px] mt-0.5">One-time payment</p>
            </div>
            <div className="text-right">
              <span className="text-white font-bold text-lg">$1.99</span>
            </div>
          </div>
          <p className="text-white/30 text-[11px] mb-3">24 Hours of Unlimited Chat Access</p>
          <ul className="space-y-1.5 mb-4">
            {['Full Mediterranean Live Chat', 'Unlimited messages during access period', 'GIFs, emojis and replies', 'One-time payment', 'No automatic renewal'].map(t => (
              <li key={t} className="flex items-center gap-2 text-white/25 text-[10px]">
                <span className="w-1 h-1 rounded-full bg-white/15 flex-shrink-0" /> {t}
              </li>
            ))}
          </ul>
          <button onClick={handleDayPass} disabled={!!loading}
            className="w-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-white font-semibold text-sm rounded-xl px-4 py-3 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="btn-day-pass">
            {loading === 'day_pass' ? <Loader2 size={14} className="animate-spin" /> : null}
            Get Day Pass — $1.99
          </button>
        </div>

        {/* Monthly Option (highlighted) */}
        <div className="relative" data-testid="option-monthly">
          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10">
            <span className="bg-[#E91E8D] text-white text-[9px] font-bold tracking-wider uppercase px-3 py-1 rounded-full">Best Value</span>
          </div>
          <div className="glass-card p-5 border-[#E91E8D]/20 bg-[#E91E8D]/[0.03]">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-bold text-sm">Monthly</h3>
                <p className="text-white/25 text-[10px] mt-0.5">Recurring subscription</p>
              </div>
              <div className="text-right">
                <span className="text-white font-bold text-lg">$4.99</span>
                <span className="text-white/30 text-xs"> / mo</span>
              </div>
            </div>
            <p className="text-white/30 text-[11px] mb-3">Unlimited Live Chat Access</p>
            <ul className="space-y-1.5 mb-4">
              {['Full Mediterranean Live Chat', 'GIFs, emojis and replies', 'Automatically renews monthly', 'Cancel anytime via Square'].map(t => (
                <li key={t} className="flex items-center gap-2 text-white/25 text-[10px]">
                  <span className="w-1 h-1 rounded-full bg-[#E91E8D]/40 flex-shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <button onClick={handleMonthly} disabled={!!loading}
              className="w-full bg-gradient-to-r from-[#E91E8D] to-[#6366F1] text-white font-semibold text-sm rounded-xl px-4 py-3 transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-[0_0_30px_rgba(233,30,141,0.2)] flex items-center justify-center gap-2"
              data-testid="btn-monthly">
              {loading === 'monthly' ? <Loader2 size={14} className="animate-spin" /> : null}
              Join Monthly — $4.99
            </button>
          </div>
        </div>

        <p className="text-white/10 text-[9px] text-center mt-4 leading-relaxed">
          Secure checkout via Square. Day Pass grants exactly 24 hours of access. Monthly renews automatically.
        </p>

        <div className="text-center mt-5 space-y-3">
          <button onClick={() => setShowRestore(true)} className="text-[#E91E8D]/60 hover:text-[#E91E8D] text-[11px] transition-colors" data-testid="btn-restore-access">
            Already paid? Restore access
          </button>
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-xs transition-colors" data-testid="chat-paywall-home">
              <ArrowLeft size={12} /> Back to Home
            </Link>
          </div>
        </div>

        {/* Restore Access Dialog — Two-Step OTP */}
        {showRestore && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
            <div className="glass-card p-5" data-testid="restore-access-dialog">
              {!otpSent ? (
                <>
                  <h3 className="text-white font-bold text-sm mb-1">Restore Your Access</h3>
                  <p className="text-white/30 text-[10px] mb-3">Enter the email you used when purchasing via Square. We'll send a verification code.</p>
                  <form onSubmit={handleRestoreRequest}>
                    <input
                      type="email"
                      value={restoreEmail}
                      onChange={(e) => setRestoreEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-[#E91E8D]/40 mb-3"
                      data-testid="restore-email-input"
                      autoFocus
                      required
                    />
                    <div className="flex gap-2">
                      <button type="submit" disabled={restoring || !restoreEmail.trim()}
                        className="flex-1 bg-[#E91E8D]/20 hover:bg-[#E91E8D]/30 border border-[#E91E8D]/30 text-white font-semibold text-xs rounded-lg px-3 py-2.5 transition-all disabled:opacity-40 flex items-center justify-center gap-2"
                        data-testid="btn-restore-send-code">
                        {restoring ? <Loader2 size={12} className="animate-spin" /> : null}
                        Send Code
                      </button>
                      <button type="button" onClick={() => { setShowRestore(false); setRestoreEmail(''); setOtpSent(false); setOtpCode(''); }}
                        className="bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/50 text-xs rounded-lg px-3 py-2.5 transition-all">
                        Cancel
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <h3 className="text-white font-bold text-sm mb-1">Enter Verification Code</h3>
                  <p className="text-white/30 text-[10px] mb-3">
                    We sent a 6-digit code to <span className="text-white/50">{restoreEmail}</span>. Check your inbox.
                  </p>
                  <form onSubmit={handleOtpVerify}>
                    <input
                      type="text"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-3 text-white text-lg font-bold text-center tracking-[0.3em] placeholder:text-white/10 placeholder:tracking-[0.3em] focus:outline-none focus:border-[#E91E8D]/40 mb-3"
                      data-testid="restore-otp-input"
                      autoFocus
                      maxLength={6}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                    />
                    <div className="flex gap-2">
                      <button type="submit" disabled={otpVerifying || otpCode.length !== 6}
                        className="flex-1 bg-[#E91E8D]/20 hover:bg-[#E91E8D]/30 border border-[#E91E8D]/30 text-white font-semibold text-xs rounded-lg px-3 py-2.5 transition-all disabled:opacity-40 flex items-center justify-center gap-2"
                        data-testid="btn-restore-verify">
                        {otpVerifying ? <Loader2 size={12} className="animate-spin" /> : null}
                        Verify & Restore
                      </button>
                      <button type="button" onClick={() => { setOtpSent(false); setOtpCode(''); }}
                        className="bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/50 text-xs rounded-lg px-3 py-2.5 transition-all"
                        data-testid="btn-restore-back">
                        Back
                      </button>
                    </div>
                    <button type="button" onClick={() => { setOtpCode(''); setOtpSent(false); }}
                      className="w-full text-white/20 hover:text-white/40 text-[10px] mt-3 transition-colors"
                      data-testid="btn-resend-code">
                      Didn't receive it? Resend code
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

/* ═══════════ Banned Screen ═══════════ */
const BannedScreen = () => (
  <div className="min-h-screen flex items-center justify-center px-4 bg-black" data-testid="chat-banned">
    <SEO title="Mediterranean Live Chat" url="/chat" />
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
      <div className="glass-card p-8 text-center">
        <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
          <ShieldAlert size={26} className="text-red-400/70" />
        </div>
        <h2 className="font-display text-xl font-bold text-white mb-3">Access Restricted</h2>
        <p className="text-white/40 text-sm leading-relaxed mb-6">
          Your access to Mediterranean Live Chat has been restricted.
        </p>
        <Link to="/" className="btn-pink !text-sm inline-flex items-center gap-2 justify-center" data-testid="chat-banned-home">
          <ArrowLeft size={14} /> Back to Home
        </Link>
      </div>
    </motion.div>
  </div>
);

/* ═══════════ Moderation Action Sheet (mobile-first) ═══════════ */
const ModSheetAction = ({ label, icon: Icon, color = 'text-white/50', onClick, suffix }) => (
  <button onClick={onClick}
    className={`w-full flex items-center gap-3 px-5 py-3.5 text-left active:bg-white/5 transition-colors ${color}`}
    data-testid={`mod-sheet-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    {Icon && <Icon size={16} />}
    <span className="flex-1 text-sm font-medium">{label}</span>
    {suffix && <span className="text-white/20 text-sm">{suffix}</span>}
  </button>
);

const ModerationSheet = ({ msg, adminRole, adminToken, onClose }) => {
  const [view, setView] = useState('actions');
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const isAdmin = adminRole === 'admin';

  const exec = async (path, body = {}) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) { toast.success('Moderation action applied'); onClose(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Action failed'); }
    } catch { toast.error('Action failed'); }
    setLoading(false);
  };

  const act = (action) => {
    if (action === 'pin') return exec(`/api/chat/admin/messages/${msg.id}/pin`);
    if (action === 'unpin') return exec(`/api/chat/admin/messages/${msg.id}/unpin`);
    if (action === 'timeout') return setView('timeout');
    setPending(action);
    setReason('');
    setView('confirm');
  };

  const pickTimeout = (m) => { setPending({ type: 'timeout', minutes: m }); setReason(''); setView('confirm'); };

  const confirm = () => {
    const r = reason.trim() || null;
    if (pending === 'delete') return exec(`/api/chat/admin/messages/${msg.id}/delete`, { reason: r });
    if (pending === 'mute') return exec(`/api/chat/admin/mute/${msg.user_id}`, { reason: r });
    if (pending === 'ban') return exec(`/api/chat/admin/ban/${msg.user_id}`, { reason: r });
    if (pending?.type === 'timeout') return exec(`/api/chat/admin/timeout/${msg.user_id}`, { duration_minutes: pending.minutes, reason: r });
  };

  const pendingLabel = pending === 'delete' ? 'Delete Message' : pending === 'mute' ? 'Mute User' :
    pending === 'ban' ? 'Ban User' : pending?.type === 'timeout' ? `Timeout ${pending.minutes < 60 ? `${pending.minutes}min` : '1h'}` : '';

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center" data-testid="mod-action-sheet">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="mod-sheet-backdrop" />
      <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} transition={{ type: 'spring', damping: 30, stiffness: 400 }}
        className="relative w-full max-w-lg bg-[#111] border-t border-white/10 rounded-t-2xl overflow-hidden z-10">
        <div className="flex justify-center pt-3 pb-1"><div className="w-8 h-1 rounded-full bg-white/20" /></div>
        <div className="px-5 pb-3 border-b border-white/5">
          <p className="text-white/20 text-[9px] font-bold tracking-wider uppercase mb-1">Moderate Message</p>
          <p className="text-white/60 text-xs font-semibold">{msg.display_name}</p>
          <p className="text-white/25 text-[10px] truncate mt-0.5">{msg.type === 'gif' ? 'GIF' : msg.type === 'image' ? (msg.content ? 'Image: ' + msg.content.slice(0, 60) : 'Image') : (msg.content || '').slice(0, 80)}</p>
        </div>

        {view === 'actions' && (
          <div className="py-1" data-testid="mod-sheet-actions">
            <ModSheetAction label="Delete Message" icon={Trash2} color="text-red-400" onClick={() => act('delete')} />
            <ModSheetAction label={msg.pinned ? 'Unpin Message' : 'Pin Message'} icon={msg.pinned ? PinOff : Pin} color="text-amber-400" onClick={() => act(msg.pinned ? 'unpin' : 'pin')} />
            <div className="h-px bg-white/5 mx-5" />
            <ModSheetAction label="Mute User" icon={Shield} color="text-amber-400" onClick={() => act('mute')} />
            <ModSheetAction label="Timeout User" icon={Clock} color="text-orange-400" onClick={() => act('timeout')} suffix="›" />
            {isAdmin && (<>
              <div className="h-px bg-white/5 mx-5" />
              <ModSheetAction label="Ban User" icon={Ban} color="text-red-400" onClick={() => act('ban')} />
            </>)}
            <div className="h-px bg-white/5 mx-5" />
            <ModSheetAction label="Cancel" color="text-white/30" onClick={onClose} />
          </div>
        )}

        {view === 'timeout' && (
          <div className="py-1" data-testid="mod-sheet-timeout">
            <p className="text-white/25 text-[10px] font-semibold px-5 py-2 uppercase tracking-wider">Select duration</p>
            {[5, 15, 30, 60].map(d => (
              <ModSheetAction key={d} label={d < 60 ? `${d} minutes` : '1 hour'} icon={Clock} color="text-orange-400" onClick={() => pickTimeout(d)} />
            ))}
            <div className="h-px bg-white/5 mx-5" />
            <ModSheetAction label="Back" color="text-white/30" onClick={() => setView('actions')} />
          </div>
        )}

        {view === 'confirm' && (
          <div className="px-5 py-4" data-testid="mod-sheet-confirm-view">
            {pending === 'ban' && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2 mb-3">
                <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
                <p className="text-red-400/80 text-[10px]">This will block the user from entering Live Chat entirely.</p>
              </div>
            )}
            <p className="text-white/50 text-xs font-semibold mb-2">{pendingLabel}</p>
            <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (recommended)..."
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-xs placeholder-white/20 outline-none resize-none focus:border-white/20 transition-colors"
              rows={2} data-testid="mod-sheet-reason" />
            <div className="flex gap-2 mt-3">
              <button onClick={() => setView(pending?.type === 'timeout' ? 'timeout' : 'actions')}
                className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/40 text-xs font-semibold" data-testid="mod-sheet-back">
                Back
              </button>
              <button onClick={confirm} disabled={loading}
                className={`flex-1 py-2.5 rounded-xl text-white text-xs font-semibold flex items-center justify-center gap-1.5 ${
                  pending === 'ban' ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'
                }`} data-testid="mod-sheet-confirm">
                {loading && <Loader2 size={12} className="animate-spin" />}
                Confirm
              </button>
            </div>
          </div>
        )}
        <div className="h-[env(safe-area-inset-bottom,0px)]" />
      </motion.div>
    </div>
  );
};

/* ═══════════ Message Bubble ═══════════ */
/* ═══════════ Message Action Menu ═══════════ */
const MessageActionMenu = ({ msg, isOwn, position, onReply, onEdit, onDelete, onModerate, adminRole, onClose }) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [onClose]);

  const actions = [];
  actions.push({ icon: Reply, label: 'Reply', key: 'reply', handler: () => { onReply(msg); onClose(); } });
  if (isOwn && msg.type === 'text' && !msg.deleted) {
    actions.push({ icon: Pencil, label: 'Edit', key: 'edit', handler: () => { onEdit(msg); onClose(); } });
  }
  if (isOwn && !msg.deleted) {
    actions.push({ icon: Trash2, label: 'Delete', key: 'delete', danger: true, handler: () => { onDelete(msg); onClose(); } });
  }
  if (adminRole && onModerate) {
    actions.push({ icon: Shield, label: 'Moderate', key: 'moderate', handler: () => { onModerate(msg); onClose(); } });
  }

  return (
    <div ref={menuRef}
      className="absolute z-50 bg-[#1a1a2e]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150"
      style={{ [position.side]: '100%', top: position.top, marginLeft: position.side === 'left' ? '-4px' : undefined, marginRight: position.side === 'right' ? '-4px' : undefined }}
      data-testid={`msg-action-menu-${msg.id}`}>
      {actions.map(a => (
        <button key={a.key} onClick={a.handler}
          className={`flex items-center gap-2.5 w-full px-3.5 py-2 text-xs font-medium transition-colors
            ${a.danger ? 'text-red-400/80 hover:bg-red-500/10' : 'text-white/70 hover:bg-white/5'}`}
          data-testid={`msg-action-${a.key}-${msg.id}`}>
          <a.icon size={13} />
          <span>{a.label}</span>
        </button>
      ))}
    </div>
  );
};

/* ═══════════ Message Bubble ═══════════ */
const MessageBubble = ({ msg, isOwn, onReply, onEdit, onDelete, adminRole, onModerate, scrollToMessage }) => {
  const time = new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const rp = msg.reply_preview;
  const isGif = msg.type === 'gif' && msg.gif_url;
  const isImage = msg.type === 'image' && msg.image_url;
  const isMedia = isGif || isImage;
  const [showMenu, setShowMenu] = useState(false);
  const pressRef = useRef(null);
  const touchRef = useRef(null);
  const bubbleRef = useRef(null);

  useEffect(() => () => { if (pressRef.current) clearTimeout(pressRef.current); }, []);

  // Long-press for mobile — opens action menu (not just moderation)
  const handleTouchStart = (e) => {
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    pressRef.current = setTimeout(() => {
      pressRef.current = null;
      setShowMenu(true);
    }, 400);
  };
  const handleTouchMove = (e) => {
    if (!pressRef.current || !touchRef.current) return;
    if (Math.abs(e.touches[0].clientX - touchRef.current.x) > 10 || Math.abs(e.touches[0].clientY - touchRef.current.y) > 10) {
      clearTimeout(pressRef.current); pressRef.current = null;
    }
  };
  const handleTouchEnd = () => {
    if (pressRef.current) { clearTimeout(pressRef.current); pressRef.current = null; }
  };

  // Click handler for desktop — single click toggles menu
  const handleClick = (e) => {
    // Don't trigger on link/button clicks or media
    if (e.target.closest('a, button, img')) return;
    e.stopPropagation();
    setShowMenu(prev => !prev);
  };

  // Deleted message display
  if (msg.deleted) {
    return (
      <div className="flex justify-center mb-2" data-testid={`chat-msg-deleted-${msg.id}`}>
        <span className="text-white/15 text-[10px] italic bg-white/[0.02] px-3 py-1.5 rounded-full">Message removed</span>
      </div>
    );
  }

  const menuPosition = isOwn ? { side: 'right', top: '0px' } : { side: 'left', top: '0px' };

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2 group`}
      id={`msg-${msg.id}`}
      data-testid={`chat-msg-${msg.id}`}
    >
      <div className="max-w-[80%] sm:max-w-[70%] relative">
        {/* Reply reference */}
        {rp && (
          <div className={`flex items-start gap-1.5 mb-1 ${isOwn ? 'justify-end' : ''}`}>
            <button
              onClick={() => rp.id && scrollToMessage?.(rp.id)}
              className="rounded-lg px-2.5 py-1.5 bg-white/[0.03] border-l-2 border-[#E91E8D]/30 max-w-full text-left hover:bg-white/[0.06] transition-colors cursor-pointer"
              data-testid={`reply-ref-${msg.id}`}>
              <span className={`text-[9px] font-bold block ${rp.is_anonymous ? 'text-white/20' : 'text-[#E91E8D]/50'}`}>
                {rp.display_name}
              </span>
              <p className="text-white/25 text-[10px] leading-tight truncate max-w-[200px]">
                {rp.content || 'Original message unavailable'}
              </p>
            </button>
          </div>
        )}

        {/* Bubble */}
        <div ref={bubbleRef}
          className={`rounded-2xl relative cursor-pointer select-none ${isMedia ? 'p-1.5' : 'px-4 py-2.5'} ${
            isOwn
              ? 'bg-[#E91E8D]/15 border border-[#E91E8D]/20'
              : 'bg-white/5 border border-white/8'
          } ${showMenu ? 'ring-1 ring-white/15' : ''} transition-all`}
          onClick={handleClick}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onContextMenu={(e) => { e.preventDefault(); setShowMenu(true); }}
        >
          {!isOwn && (
            <span className={`text-[10px] font-bold block mb-0.5 ${isMedia ? 'px-2 pt-1' : ''} ${msg.is_anonymous ? 'text-white/25' : 'text-[#E91E8D]/70'}`}>
              {msg.display_name}
            </span>
          )}

          {isGif ? (
            <img src={msg.gif_url} alt="GIF" loading="lazy"
              className="rounded-xl max-w-full max-h-[240px] object-contain"
              data-testid={`gif-img-${msg.id}`} />
          ) : isImage ? (
            <>
              <img src={msg.image_url} alt="Shared image" loading="lazy"
                className="rounded-xl max-w-full max-h-[300px] object-contain cursor-pointer"
                onClick={(e) => { e.stopPropagation(); window.open(msg.image_url, '_blank'); }}
                data-testid={`chat-img-${msg.id}`} />
              {msg.content && (
                <p className="text-white/80 text-sm leading-relaxed break-words whitespace-pre-wrap px-2 pt-1.5">{msg.content}</p>
              )}
            </>
          ) : (
            <p className="text-white/80 text-sm leading-relaxed break-words whitespace-pre-wrap">{msg.content}</p>
          )}

          <div className={`flex items-center justify-end gap-1.5 mt-1 ${isMedia ? 'px-1.5 pb-0.5' : ''}`}>
            {msg.edited_at && (
              <span className="text-white/15 text-[8px] italic" data-testid={`edited-indicator-${msg.id}`}>edited</span>
            )}
            <span className="text-white/15 text-[9px]">{time}</span>
          </div>
        </div>

        {/* Contextual action menu */}
        {showMenu && (
          <MessageActionMenu
            msg={msg} isOwn={isOwn} position={menuPosition}
            onReply={onReply} onEdit={onEdit} onDelete={onDelete}
            onModerate={onModerate} adminRole={adminRole}
            onClose={() => setShowMenu(false)}
          />
        )}
      </div>
    </div>
  );
};

/* ═══════════ Reply Preview Bar ═══════════ */
const ReplyPreview = ({ msg, onCancel }) => (
  <div className="flex items-center gap-2 px-3 py-2 bg-white/[0.03] border-l-2 border-[#E91E8D]/40 rounded-t-xl" data-testid="reply-preview">
    <Reply size={12} className="text-[#E91E8D]/50 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <span className="text-[9px] font-bold text-[#E91E8D]/50 block">Replying to {msg.display_name}</span>
      <p className="text-white/30 text-[10px] truncate">{msg.type === 'gif' ? 'GIF' : msg.type === 'image' ? 'Image' : msg.content}</p>
    </div>
    <button onClick={onCancel} className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-white/20 hover:text-white/50 transition-colors" data-testid="cancel-reply">
      <X size={14} />
    </button>
  </div>
);

/* ═══════════ Edit Preview Bar ═══════════ */
const EditPreview = ({ msg, onCancel }) => (
  <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/[0.04] border-l-2 border-amber-400/40 rounded-t-xl" data-testid="edit-preview">
    <Pencil size={12} className="text-amber-400/60 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <span className="text-[9px] font-bold text-amber-400/60 block">Editing message</span>
      <p className="text-white/30 text-[10px] truncate">{msg.content}</p>
    </div>
    <button onClick={onCancel} className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-white/20 hover:text-white/50 transition-colors" data-testid="cancel-edit">
      <X size={14} />
    </button>
  </div>
);

/* ═══════════ Typing Indicator ═══════════ */
const TypingIndicator = ({ typingUsers }) => {
  const names = Object.values(typingUsers).map(u => u.display_name);
  if (names.length === 0) return null;

  let text;
  if (names.length === 1) text = `${names[0]} is typing`;
  else if (names.length === 2) text = `${names[0]} and ${names[1]} are typing`;
  else text = 'Several people are typing';

  return (
    <div className="px-3 py-1.5" data-testid="typing-indicator">
      <div className="max-w-2xl mx-auto flex items-center gap-2">
        <div className="flex gap-0.5">
          <span className="w-1 h-1 rounded-full bg-white/30 animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-1 h-1 rounded-full bg-white/30 animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-1 h-1 rounded-full bg-white/30 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
        <span className="text-white/25 text-[10px] italic">{text}</span>
      </div>
    </div>
  );
};

/* ═══════════ Main Chat View ═══════════ */
const ChatView = ({ user, onBanned, onAccessExpired }) => {
  const { messages, connected, onlineCount, typingUsers, sendTyping, fetchOlderMessages, pinnedMessages, lastModEvent } = useChat(user.id, user.display_name, onAccessExpired);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [replyTo, setReplyTo] = useState(null);
  const [editingMsg, setEditingMsg] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showGif, setShowGif] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // { file, previewUrl }
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isMuted, setIsMuted] = useState(user.is_muted || false);
  const [mutedUntil, setMutedUntil] = useState(user.muted_until || null);
  const [adminRole] = useState(() => getAdminRole());
  const adminToken = adminRole ? localStorage.getItem('med_admin_token') : null;
  const [modSheet, setModSheet] = useState(null);
  const bottomRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const isNearBottom = useRef(true);
  const prevMsgCount = useRef(0);

  // Handle real-time moderation events for this user
  useEffect(() => {
    if (!lastModEvent || lastModEvent.user_id !== user.id) return;
    const { action, expires_at } = lastModEvent;
    if (action === 'banned') { onBanned(); return; }
    if (action === 'muted' || action === 'timed_out') { setIsMuted(true); setMutedUntil(expires_at || null); }
    else if (action === 'unmuted' || action === 'unbanned') { setIsMuted(false); setMutedUntil(null); }
  }, [lastModEvent, user.id, onBanned]);

  // Auto-clear expired timeout
  useEffect(() => {
    if (!isMuted || !mutedUntil) return;
    const ms = new Date(mutedUntil).getTime() - Date.now();
    if (ms <= 0) { setIsMuted(false); setMutedUntil(null); return; }
    const timer = setTimeout(() => { setIsMuted(false); setMutedUntil(null); }, ms);
    return () => clearTimeout(timer);
  }, [isMuted, mutedUntil]);

  const scrollToBottom = useCallback((force = false) => {
    if (force || isNearBottom.current) {
      bottomRef.current?.scrollIntoView({ behavior: force ? 'auto' : 'smooth' });
    }
  }, []);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    isNearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  useEffect(() => {
    if (messages.length > prevMsgCount.current) scrollToBottom();
    prevMsgCount.current = messages.length;
  }, [messages, scrollToBottom]);

  useEffect(() => { scrollToBottom(true); }, [scrollToBottom]);

  const loadMore = async () => {
    if (loadingOlder || !hasMore) return;
    const el = containerRef.current;
    const prevHeight = el?.scrollHeight || 0;
    setLoadingOlder(true);
    const more = await fetchOlderMessages();
    setHasMore(more);
    setLoadingOlder(false);
    requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - prevHeight; });
  };

  const handleReply = useCallback((msg) => {
    setReplyTo(msg);
    setEditingMsg(null);
    setShowEmoji(false);
    setShowGif(false);
    inputRef.current?.focus();
  }, []);

  const handleEdit = useCallback((msg) => {
    setEditingMsg(msg);
    setReplyTo(null);
    setInput(msg.content || '');
    setShowEmoji(false);
    setShowGif(false);
    setPendingImage(null);
    inputRef.current?.focus();
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingMsg(null);
    setInput('');
  }, []);

  const handleDeleteOwn = useCallback(async (msg) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      const res = await fetch(`${API}/api/chat/messages/${msg.id}?user_id=${user.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const d = await res.json();
        toast.error(d.detail || 'Failed to delete');
      }
    } catch { toast.error('Failed to delete message'); }
  }, [user.id]);

  const scrollToMessage = useCallback((msgId) => {
    const el = document.getElementById(`msg-${msgId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-1', 'ring-[#E91E8D]/30');
      setTimeout(() => el.classList.remove('ring-1', 'ring-[#E91E8D]/30'), 1500);
    }
  }, []);

  const handleEmojiSelect = useCallback((emoji) => {
    const textarea = inputRef.current;
    if (!textarea) { setInput(prev => prev + emoji); return; }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = input.substring(0, start);
    const after = input.substring(end);
    const newVal = before + emoji + after;
    setInput(newVal);
    requestAnimationFrame(() => {
      textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
      textarea.focus();
    });
  }, [input]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = input.trim();
    const hasImage = !!pendingImage;

    // EDIT MODE: save the edit instead of sending a new message
    if (editingMsg) {
      if (!text) return;
      setSending(true);
      try {
        const res = await fetch(`${API}/api/chat/messages/${editingMsg.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: user.id, content: text }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed to edit');
        setEditingMsg(null);
        setInput('');
      } catch (err) {
        toast.error(err.message);
      }
      setSending(false);
      return;
    }

    if ((!text && !hasImage) || sending || uploadingImage) return;
    setSending(true);

    try {
      if (hasImage) {
        // Upload image first, then send image message
        setUploadingImage(true);
        const formData = new FormData();
        formData.append('user_id', user.id);
        formData.append('file', pendingImage.file);
        const uploadRes = await fetch(`${API}/api/chat/upload-image`, {
          method: 'POST', body: formData,
        });
        const uploadData = await uploadRes.json();
        if (!uploadRes.ok) throw new Error(uploadData.detail || 'Image upload failed');
        setUploadingImage(false);

        // Send image message with optional caption
        const body = { user_id: user.id, image_url: uploadData.url };
        if (text) body.content = text;
        if (replyTo) body.reply_to_id = replyTo.id;
        const res = await fetch(`${API}/api/chat/messages/image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const msgData = await res.json();
        if (!res.ok) throw new Error(msgData.detail || 'Failed to send image');
      } else {
        // Text-only message
        const body = { user_id: user.id, content: text };
        if (replyTo) body.reply_to_id = replyTo.id;
        const res = await fetch(`${API}/api/chat/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed to send');
      }
      setInput('');
      setReplyTo(null);
      setEditingMsg(null);
      setPendingImage(null);
      setShowEmoji(false);
      setShowGif(false);
      isNearBottom.current = true;
    } catch (err) {
      toast.error(err.message);
      setUploadingImage(false);
    }
    setSending(false);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG, and WEBP images are allowed.');
      return;
    }

    // Validate size
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5 MB or smaller.');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setPendingImage({ file, previewUrl });
    setShowGif(false);
    setShowEmoji(false);
    // Reset file input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const cancelImage = () => {
    if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage(null);
  };

  const charWarning = input.length > MAX_MSG_LENGTH * 0.9;

  return (
    <div className="h-screen flex flex-col bg-black" data-testid="chat-view">
      <SEO title="Mediterranean Live Chat" description="Mediterranean Live Chat — real-time conversation." url="/chat" />

      {/* Header */}
      <div className="flex-shrink-0 px-4 pt-20 pb-3 border-b border-white/5 bg-black/80 backdrop-blur-xl z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-white/30 hover:text-white transition-colors" data-testid="chat-header-back">
              <ArrowLeft size={18} />
            </Link>
            <div>
              <h1 className="text-white font-bold text-sm" data-testid="chat-header-title">Mediterranean Live Chat</h1>
              <div className="flex items-center gap-1.5">
                {connected ? (
                  <span className="text-green-400/50 text-[9px] flex items-center gap-1" data-testid="chat-connected">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> Live
                  </span>
                ) : (
                  <span className="text-amber-400/60 text-[9px] flex items-center gap-1" data-testid="chat-reconnecting">
                    <WifiOff size={9} /> Reconnecting...
                  </span>
                )}
                {connected && onlineCount > 0 && (
                  <>
                    <span className="text-white/10 text-[9px]">·</span>
                    <span className="text-white/30 text-[9px] flex items-center gap-1" data-testid="online-count">
                      <Users size={9} /> {onlineCount} {onlineCount === 1 ? 'person' : 'people'} chatting
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <span className="text-white/25 text-[9px] block">Chatting as</span>
            <span className={`text-xs font-semibold ${user.is_anonymous ? 'text-white/40' : 'text-[#E91E8D]/70'}`}>
              {user.display_name}
            </span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={containerRef} onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-4 overscroll-contain" data-testid="chat-messages-container">
        <div className="max-w-2xl mx-auto">
          {hasMore && messages.length > 0 && (
            <div className="text-center mb-4">
              <button onClick={loadMore} disabled={loadingOlder}
                className="text-white/20 text-[10px] hover:text-white/40 transition-colors" data-testid="chat-load-more">
                {loadingOlder ? 'Loading...' : 'Load older messages'}
              </button>
            </div>
          )}
          {messages.length === 0 && (
            <div className="text-center py-20">
              <MessageCircle size={32} className="text-white/10 mx-auto mb-3" />
              <p className="text-white/20 text-sm">No messages yet</p>
              <p className="text-white/10 text-xs mt-1">Be the first to say something</p>
            </div>
          )}
          {/* Pinned messages */}
          {pinnedMessages.length > 0 && (
            <div className="bg-amber-500/[0.04] border border-amber-500/10 rounded-xl px-3 py-2.5 mb-4" data-testid="pinned-messages-banner">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Pin size={10} className="text-amber-400/70" />
                <span className="text-amber-400/60 text-[9px] font-bold tracking-wider uppercase">Pinned</span>
              </div>
              {pinnedMessages.map(pm => (
                <div key={pm.id} className="text-xs mb-1 last:mb-0" data-testid={`pinned-msg-${pm.id}`}>
                  <span className="text-white/30 font-semibold">{pm.display_name}: </span>
                  <span className="text-white/45">{pm.type === 'gif' ? 'GIF' : pm.type === 'image' ? 'Image' : (pm.content || '').slice(0, 120)}{pm.type === 'text' && (pm.content || '').length > 120 ? '...' : ''}</span>
                </div>
              ))}
            </div>
          )}
          {messages.map(msg => (
            <MessageBubble key={msg.id} msg={msg} isOwn={msg.user_id === user.id} onReply={handleReply}
              onEdit={handleEdit} onDelete={handleDeleteOwn}
              adminRole={adminRole} onModerate={setModSheet} scrollToMessage={scrollToMessage} />
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Typing indicator + Composer */}
      <div className="flex-shrink-0 border-t border-white/5 bg-black/90 backdrop-blur-xl relative" data-testid="chat-composer">
        {isMuted ? (
          <div className="flex items-center justify-center gap-2 px-4 py-4" data-testid="chat-muted-banner">
            <ShieldAlert size={14} className="text-amber-400/60" />
            <span className="text-white/30 text-xs">
              {mutedUntil
                ? `You are timed out until ${new Date(mutedUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                : 'You are muted by a moderator.'}
            </span>
          </div>
        ) : (
        <>
        {showGif && (
          <GifSelector
            userId={user.id}
            onSend={() => { setShowGif(false); setReplyTo(null); setEditingMsg(null); isNearBottom.current = true; }}
            onClose={() => setShowGif(false)}
            replyToId={replyTo?.id}
          />
        )}
        <TypingIndicator typingUsers={typingUsers} />
        {replyTo && !editingMsg && <ReplyPreview msg={replyTo} onCancel={() => setReplyTo(null)} />}
        {editingMsg && <EditPreview msg={editingMsg} onCancel={cancelEdit} />}
        {/* Image preview */}
        {pendingImage && (
          <div className="max-w-2xl mx-auto px-3 pt-2" data-testid="image-preview-bar">
            <div className="relative inline-block">
              <img src={pendingImage.previewUrl} alt="Preview"
                className="h-20 max-w-[160px] object-cover rounded-lg border border-white/10" data-testid="image-preview-thumb" />
              <button onClick={cancelImage}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                data-testid="image-preview-cancel">
                <X size={10} className="text-white" />
              </button>
              {uploadingImage && (
                <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
                  <Loader2 size={16} className="animate-spin text-[#E91E8D]" />
                </div>
              )}
            </div>
          </div>
        )}
        <form onSubmit={handleSend} className="max-w-2xl mx-auto flex items-end gap-1.5 px-3 py-3 pb-safe">
          {/* Hidden file input */}
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
            onChange={handleImageSelect} className="hidden" data-testid="image-file-input" />
          {/* Image attach button */}
          <button type="button" onClick={() => { fileInputRef.current?.click(); }}
            className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${pendingImage ? 'bg-[#E91E8D]/20 text-[#E91E8D]' : 'bg-white/5 text-white/30 hover:text-white/50'}`}
            data-testid="image-attach-btn">
            <ImagePlus size={17} />
          </button>
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={input} onChange={e => { setInput(e.target.value); sendTyping(); }}
              placeholder={editingMsg ? "Edit your message..." : pendingImage ? "Add a caption..." : "Type a message..."}
              maxLength={MAX_MSG_LENGTH}
              rows={1}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); }
                if (e.key === 'Escape') { if (editingMsg) { cancelEdit(); } else if (replyTo) { setReplyTo(null); } }
              }}
              className="input-med w-full !py-2.5 !pr-12 !text-sm resize-none min-h-[40px] max-h-[120px]"
              style={{ height: 'auto', overflow: 'auto' }}
              onInput={e => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
              data-testid="chat-input"
            />
            {charWarning && (
              <span className={`absolute bottom-1.5 right-2 text-[9px] ${input.length >= MAX_MSG_LENGTH ? 'text-red-400' : 'text-amber-400/60'}`}
                data-testid="chat-char-counter">
                {input.length}/{MAX_MSG_LENGTH}
              </span>
            )}
          </div>
          {/* GIF button */}
          <button type="button" onClick={() => { setShowGif(v => !v); setShowEmoji(false); }}
            className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-[10px] font-black tracking-tight transition-colors ${showGif ? 'bg-[#E91E8D]/20 text-[#E91E8D]' : 'bg-white/5 text-white/30 hover:text-white/50'}`}
            data-testid="gif-btn">
            <Type size={16} />
          </button>
          {/* Emoji button */}
          <div className="relative flex-shrink-0">
            {showEmoji && (
              <LazyEmojiPicker
                onSelect={handleEmojiSelect}
                onClose={() => setShowEmoji(false)}
              />
            )}
            <button type="button" onClick={() => { setShowEmoji(v => !v); setShowGif(false); }}
              className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${showEmoji ? 'bg-[#E91E8D]/20 text-[#E91E8D]' : 'bg-white/5 text-white/30 hover:text-white/50'}`}
              data-testid="emoji-btn">
              <Smile size={18} />
            </button>
          </div>
          <button type="submit" disabled={(sending || uploadingImage) || (!input.trim() && !pendingImage && !editingMsg)}
            className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#E91E8D] hover:bg-[#E91E8D]/80 disabled:bg-white/5 disabled:text-white/20 text-white flex items-center justify-center transition-colors"
            data-testid="chat-send-btn">
            {(sending || uploadingImage) ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </form>
        </>
        )}
      </div>

      {/* Moderation bottom sheet (admin/mod only) */}
      {modSheet && adminRole && adminToken && (
        <ModerationSheet msg={modSheet} adminRole={adminRole} adminToken={adminToken} onClose={() => setModSheet(null)} />
      )}
    </div>
  );
};

/* ═══════════ Page Wrapper ═══════════ */
const ChatPage = () => {
  const [user, setUser] = useState(null);
  const [accessState, setAccessState] = useState(null);
  const [chatEnabled, setChatEnabled] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const [dayPassExpiresAt, setDayPassExpiresAt] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/chat/status`).then(r => r.json())
      .then(d => setChatEnabled(d.chat_enabled))
      .catch(() => setChatEnabled(true));
  }, []);

  useEffect(() => {
    // Step 1: Try cookie-based identity (server authoritative via med_uid)
    fetch(`${API}/api/chat/me`, { credentials: 'same-origin' })
      .then(r => r.json())
      .then(async (meData) => {
        if (meData.user) {
          // Cookie user found — server is source of truth
          // If the server redirected us (archived identity), update localStorage
          if (meData.redirected_from) {
            // Server swapped our cookie from archived ID to canonical ID
            localStorage.setItem(STORAGE_KEY, JSON.stringify(meData.user));
          }
          setUser(meData.user);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(meData.user));
          if (meData.user.is_banned) {
            setIsBanned(true);
            setAccessState('banned');
          } else if (meData.has_access) {
            setAccessState('active');
            if (meData.day_pass_expires_at) setDayPassExpiresAt(meData.day_pass_expires_at);
          } else {
            setAccessState('paywall');
          }
          return;
        }

        // Step 2: No cookie — try localStorage legacy migration
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
          setAccessState('no_user');
          return;
        }

        try {
          const parsed = JSON.parse(stored);
          // Access check sets the med_uid cookie as a side effect
          const [userData, accessData] = await Promise.all([
            fetch(`${API}/api/chat/user/${parsed.id}`).then(r => r.ok ? r.json() : null),
            fetch(`${API}/api/chat/access/${parsed.id}`, { credentials: 'same-origin' }).then(r => r.ok ? r.json() : null),
          ]);

          if (!userData) {
            localStorage.removeItem(STORAGE_KEY);
            setAccessState('no_user');
            return;
          }
          // If access check returned a redirect (archived identity resolved to canonical)
          if (accessData && accessData.redirect_to) {
            const redirectMe = await fetch(`${API}/api/chat/me`, { credentials: 'same-origin' }).then(r => r.json());
            if (redirectMe.user) {
              setUser(redirectMe.user);
              localStorage.setItem(STORAGE_KEY, JSON.stringify(redirectMe.user));
              if (redirectMe.has_access) {
                setAccessState('active');
                if (redirectMe.day_pass_expires_at) setDayPassExpiresAt(redirectMe.day_pass_expires_at);
              } else {
                setAccessState('paywall');
              }
              return;
            }
          }
          setUser(userData);
          if (userData.is_banned || (accessData && accessData.is_banned)) {
            setIsBanned(true);
            setAccessState('banned');
          } else if (accessData && accessData.has_access) {
            setAccessState('active');
          } else {
            setAccessState('paywall');
          }
        } catch {
          localStorage.removeItem(STORAGE_KEY);
          setAccessState('no_user');
        }
      })
      .catch(() => {
        // /me failed — fall back to localStorage
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) { setAccessState('no_user'); return; }
        try {
          const parsed = JSON.parse(stored);
          Promise.all([
            fetch(`${API}/api/chat/user/${parsed.id}`).then(r => r.ok ? r.json() : null),
            fetch(`${API}/api/chat/access/${parsed.id}`, { credentials: 'same-origin' }).then(r => r.ok ? r.json() : null),
          ]).then(([userData, accessData]) => {
            if (!userData) { localStorage.removeItem(STORAGE_KEY); setAccessState('no_user'); return; }
            setUser(userData);
            if (userData.is_banned || (accessData && accessData.is_banned)) { setIsBanned(true); setAccessState('banned'); }
            else if (accessData && accessData.has_access) setAccessState('active');
            else setAccessState('paywall');
          }).catch(() => { localStorage.removeItem(STORAGE_KEY); setAccessState('no_user'); });
        } catch { localStorage.removeItem(STORAGE_KEY); setAccessState('no_user'); }
      });
  }, []);

  // === ACCESS ENFORCEMENT: periodic re-check + day-pass expiration timer ===
  useEffect(() => {
    if (accessState !== 'active' || !user) return;
    let expirationTimer = null;
    let periodicTimer = null;

    const recheckAccess = async () => {
      try {
        const res = await fetch(`${API}/api/chat/access/${user.id}`, { credentials: 'same-origin' });
        if (!res.ok) return;
        const data = await res.json();
        // If redirected (identity consolidation), reload from server
        if (data.redirect_to) {
          const meRes = await fetch(`${API}/api/chat/me`, { credentials: 'same-origin' });
          const meData = await meRes.json();
          if (meData.user) {
            setUser(meData.user);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(meData.user));
            if (meData.has_access) return; // still active, no change
          }
        }
        if (!data.has_access) {
          setAccessState('paywall');
          setDayPassExpiresAt(null);
        }
      } catch { /* network error — don't kick out, retry next cycle */ }
    };

    // If day pass: set a precise timer for the expiration moment
    if (dayPassExpiresAt) {
      const expiresMs = new Date(dayPassExpiresAt).getTime() - Date.now();
      if (expiresMs <= 0) {
        // Already expired — re-check immediately
        recheckAccess();
      } else {
        expirationTimer = setTimeout(recheckAccess, Math.min(expiresMs + 2000, 2147483647));
      }
    }

    // Periodic re-check every 5 minutes (catches any edge cases)
    periodicTimer = setInterval(recheckAccess, 5 * 60 * 1000);

    // Visibility change: re-check when user returns to the tab
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') recheckAccess();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (expirationTimer) clearTimeout(expirationTimer);
      if (periodicTimer) clearInterval(periodicTimer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [accessState, user, dayPassExpiresAt]);


  const handleRegister = async (displayName, isAnonymous) => {
    setRegistering(true);
    try {
      const res = await fetch(`${API}/api/chat/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          display_name: isAnonymous ? null : displayName,
          is_anonymous: isAnonymous,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Registration failed');

      // If the backend redirected to a canonical identity with active access,
      // skip the paywall and go directly to chat.
      // Check redirect_to + subscription_status=active (has_access is computed, not stored)
      if (data.redirect_to && (data.has_access || data.subscription_status === 'active')) {
        const meRes = await fetch(`${API}/api/chat/me`, { credentials: 'same-origin' });
        const meData = await meRes.json();
        if (meData.user && meData.has_access) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(meData.user));
          setUser(meData.user);
          if (meData.day_pass_expires_at) setDayPassExpiresAt(meData.day_pass_expires_at);
          setAccessState('active');
          setRegistering(false);
          return;
        }
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setUser(data);
      setAccessState('paywall');
    } catch (err) {
      toast.error(err.message);
    }
    setRegistering(false);
  };

  const handleAccessGranted = useCallback((accessData) => {
    // If recovery or redirect returned user info directly, use it
    if (accessData?.user) {
      setUser(accessData.user);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(accessData.user));
      if (accessData.day_pass_expires_at) setDayPassExpiresAt(accessData.day_pass_expires_at);
      setAccessState('active');
      return;
    }
    // If redirect was indicated, reload identity from server
    if (accessData?.redirect_to) {
      fetch(`${API}/api/chat/me`, { credentials: 'same-origin' })
        .then(r => r.json()).then(meData => {
          if (meData.user) {
            setUser(meData.user);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(meData.user));
          }
          if (meData.day_pass_expires_at) setDayPassExpiresAt(meData.day_pass_expires_at);
          setAccessState('active');
        }).catch(() => setAccessState('active'));
      return;
    }
    if (user) {
      fetch(`${API}/api/chat/access/${user.id}`, { credentials: 'same-origin' })
        .then(r => r.json()).then(a => {
          if (a.day_pass_expires_at) setDayPassExpiresAt(a.day_pass_expires_at);
          // If access check reveals redirect, reload identity
          if (a.redirect_to) {
            fetch(`${API}/api/chat/me`, { credentials: 'same-origin' })
              .then(r2 => r2.json()).then(meData => {
                if (meData.user) {
                  setUser(meData.user);
                  localStorage.setItem(STORAGE_KEY, JSON.stringify(meData.user));
                }
              });
          }
        });
      fetch(`${API}/api/chat/user/${user.id}`).then(r => r.json()).then(u => {
        setUser(u);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
      });
    }
    setAccessState('active');
  }, [user]);

  const handleAccessExpired = useCallback(() => {
    setAccessState('paywall');
    setDayPassExpiresAt(null);
  }, []);

  if (chatEnabled === null || accessState === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <Loader2 size={24} className="animate-spin text-[#E91E8D]/40" />
      </div>
    );
  }

  if (!chatEnabled) return <ChatClosedScreen />;
  if (isBanned || accessState === 'banned') return <BannedScreen />;
  if (accessState === 'no_user') return <IdentityScreen onRegister={handleRegister} loading={registering} />;
  if (accessState === 'paywall' && user) return <PaywallScreen user={user} onAccessGranted={handleAccessGranted} />;
  if (accessState === 'active' && user) return <ChatView user={user} onBanned={() => setIsBanned(true)} onAccessExpired={handleAccessExpired} />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <Loader2 size={24} className="animate-spin text-[#E91E8D]/40" />
    </div>
  );
};

export default ChatPage;
